/**
 * Store-wide reviews for the Customer Reviews page and the homepage strip.
 * Pages through Judge.me's public storefront feed and renders each review as text
 * nodes in the theme's own markup. "With photos" switches the feed to photo reviews;
 * the strip starts on photo reviews (data-sort) and shows a single page.
 */
const FEED = 'https://judge.me/reviews/all_reviews_js_based';

const STAR =
  '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="M10 1.5l2.6 5.6 6.1.7-4.5 4.2 1.2 6.1L10 15.1l-5.4 3 1.2-6.1L1.3 7.8l6.1-.7z" fill="currentColor"/></svg>';

class MlbReviews extends HTMLElement {
  #page = 0;
  #sort = '';
  #controller = null;

  connectedCallback() {
    this.list = this.querySelector('[data-mlb-list]');
    this.status = this.querySelector('[data-mlb-status]');
    this.more = this.querySelector('[data-mlb-more]');
    this.#sort = this.dataset.sort || '';

    const count = this.querySelector('[data-mlb-count]');
    if (count) count.textContent = Number(count.dataset.mlbCount).toLocaleString('en-GB');

    this.more?.addEventListener('click', () => this.#load());
    for (const tab of this.querySelectorAll('[role="tab"]')) {
      tab.addEventListener('click', () => this.#switchTo(tab));
    }

    this.#load();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {Element} tab */
  #switchTo(tab) {
    if (!(tab instanceof HTMLElement) || tab.getAttribute('aria-selected') === 'true') return;
    for (const other of this.querySelectorAll('[role="tab"]')) {
      other.setAttribute('aria-selected', String(other === tab));
    }
    this.#sort = tab.dataset.sort || '';
    this.#page = 0;
    this.list.replaceChildren();
    this.#load();
  }

  async #load() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const perPage = Number(this.dataset.perPage) || 12;
    const url = new URL(FEED);
    url.search = new URLSearchParams({
      shop_domain: this.dataset.shop,
      platform: 'shopify',
      review_type: 'product-reviews',
      per_page: String(perPage),
      page: String(this.#page + 1),
      ...(this.#sort ? { sort_by: this.#sort } : {}),
    }).toString();

    if (this.more) this.more.hidden = true;
    this.status.textContent = 'Loading reviews…';

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      const doc = new DOMParser().parseFromString(data.html || '', 'text/html');
      const reviews = [...doc.querySelectorAll('.jdgm-rev')];

      for (const review of reviews.slice(0, perPage)) this.list.append(this.#card(review));

      this.#page += 1;
      this.status.textContent = this.list.children.length ? '' : 'No reviews to show yet.';
      if (this.more) this.more.hidden = reviews.length === 0;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.status.textContent = 'Reviews could not be loaded. Please refresh the page.';
    }
  }

  /**
   * Builds one review card from Judge.me's markup using text only.
   * @param {Element} review
   */
  #card(review) {
    const text = (selector) => review.querySelector(selector)?.textContent.replace(/\s+/g, ' ').trim() || '';
    const score = Math.max(0, Math.min(5, Number(review.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')) || 5));

    const card = document.createElement('article');
    card.className = 'mlb-review';

    const top = document.createElement('div');
    top.className = 'mlb-review__top';
    const stars = document.createElement('div');
    stars.className = 'mlb-review__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${score} out of 5 stars`);
    stars.innerHTML = STAR.repeat(score);
    top.append(stars);

    const stamp = review.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content');
    const date = stamp ? new Date(stamp.replace(' UTC', 'Z').replace(' ', 'T')) : null;
    if (date && !Number.isNaN(date.getTime())) {
      const time = document.createElement('time');
      time.className = 'mlb-review__date';
      time.dateTime = date.toISOString();
      time.textContent = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      top.append(time);
    }
    card.append(top);

    const title = text('.jdgm-rev__title');
    if (title) {
      const heading = document.createElement('p');
      heading.className = 'mlb-review__title';
      heading.textContent = title;
      card.append(heading);
    }

    const bodyText = text('.jdgm-rev__body');
    if (bodyText) {
      const body = document.createElement('p');
      body.className = 'mlb-review__body';
      body.textContent = bodyText;
      card.append(body);
    }

    const pics = [...review.querySelectorAll('.jdgm-rev__pic-link')].slice(0, 4);
    if (pics.length) {
      const photos = document.createElement('div');
      photos.className = 'mlb-review__photos';
      for (const pic of pics) {
        const thumb = pic.querySelector('img')?.getAttribute('data-src') || pic.getAttribute('href');
        if (!thumb) continue;
        const link = document.createElement('a');
        link.href = pic.getAttribute('href') || thumb;
        link.target = '_blank';
        link.rel = 'noopener nofollow';
        const img = document.createElement('img');
        img.src = thumb.replace(/width=\d+/, 'width=320');
        img.alt = 'Customer photo';
        img.loading = 'lazy';
        img.width = 160;
        img.height = 160;
        link.append(img);
        photos.append(link);
      }
      card.append(photos);
    }

    const meta = document.createElement('div');
    meta.className = 'mlb-review__meta';
    const author = text('.jdgm-rev__author');
    if (author) {
      const parts = author.split(' ').filter(Boolean);
      const name = document.createElement('span');
      name.textContent = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
      meta.append(name);
    }
    const productLink = review.querySelector('.jdgm-rev__prod-link');
    if (productLink) {
      const product = document.createElement('a');
      product.href = productLink.getAttribute('href') || '#';
      product.textContent = (productLink.textContent || '')
        .replace(/[‘’'"“”]/g, '')
        .replace(/\s+X9X\b.*$/i, '')
        .trim();
      if (product.textContent) meta.append(product);
    }
    card.append(meta);

    return card;
  }
}

if (!customElements.get('mlb-reviews')) customElements.define('mlb-reviews', MlbReviews);
